export const ENDPOINTS = {
  // Auth
  LOGIN: '/auth/login',
  SSO_LOGIN: '/auth/login/sso',
  // Generic provider-based SSO endpoint — used by Google SSO (and any future providers).
  // Maps to POST /auth/login/sso/:provider on the backend.
  SSO_LOGIN_PROVIDER: (provider) => `/auth/login/sso/${provider}`,
  TENANT_INFO: (domain) => `/auth/tenant/${domain}`,
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',
  
  // Users
  USERS: '/users',
  USERS_ALL: '/users/all',
  USER_BY_ID: (id) => `/users/${id}`,
  ROLES: '/roles',
  
  // Spaces
  SPACES: '/spaces',
  SPACES_WIDGETS: '/spaces/widgets',
  SPACE_BY_ID: (id) => `/spaces/${id}`,
  
  // Projects
  PROJECTS: '/projects/',
  PROJECTS_DROPDOWNS: '/projects/dropdowns',
  SCRUM_CREATE_SPRINT: (projectId) => `/scrum/${projectId}/sprints`,
  SCRUM_UPDATE_SPRINT: (sprintId) => `/scrum/sprints/${sprintId}`,
  PROJECTS_DROPDOWNS_BY_ID: (projectId) => `/projects/dropdowns?project_id=${projectId}`,
  PROJECTS_EXISTING_PREFIXES: '/projects/existing-prefixes',
  PROJECT_BY_ID: (id) => `/projects/${id}`,
  
  // Tickets
  TICKETS: '/tickets',
  TICKETS_BY_PROJECT: (projectId) => `/tickets/${projectId}`,
  TICKETS_UPDATE: (ticketId) => `/tickets/${ticketId}`,
  TICKETS_DELETE: (ticketId) => `/tickets/${ticketId}`,
  TICKETS_COMMENTS: (ticketId) => `/tickets/${ticketId}/comments`,
  COMMENTS_UPDATE: (commentId) => `/tickets/comments/${commentId}`,
  COMMENTS_DELETE: (commentId) => `/tickets/comments/${commentId}`,
  TICKETS_WORKLOGS: (ticketId) => `/tickets/${ticketId}/worklogs`,
  TICKETS_WORKLOG_UPDATE: (worklogId) => `/tickets/worklogs/${worklogId}`,
  TICKETS_WORKLOG_DELETE: (worklogId) => `/tickets/worklogs/${worklogId}`,
  TICKETS_SUMMARY: (ticketId) => `/tickets/${ticketId}/summary`,
  TICKETS_HISTORY: (ticketId) => `/tickets/${ticketId}/history`,
  TICKETS_DETAILS: (ticketId) => `/tickets/details/${ticketId}`,
  TICKETS_ID_BY_NUMBER: (ticketNumber) => `/tickets/id-by-number/${ticketNumber}`,
  TICKETS_SUBTASKS: (ticketId) => `/tickets/subtasks/${ticketId}`,
  TICKETS_BULK_ACTIONS: (projectId) => `/tickets/bulk-actions/${projectId}`,
  LINK_TICKETS: (projectId) => `/tickets/link-tickets/${projectId}`,
  UPDATE_LINK_TICKETS: (relationshipId) => `/tickets/link-tickets/${relationshipId}`,
  DELETE_LINK_TICKETS: (relationshipId) => `/tickets/link-tickets/${relationshipId}`,
  SEARCH_TICKETS: (projectId) => `/tickets/search/${projectId}`,
  SEARCH_SPRINTS: (projectId) => `/tickets/search-sprints/${projectId}`,
  
  // File endpoints
  FILE_PRE_UPLOAD: '/file/pre/upload',
  FILE_CONFIRM_UPLOAD: '/file/post/upload',
  FILE_DETAILS: (fileId) => `/file/${fileId}`,
  TICKETS_DROPDOWNS: '/tickets/dropdowns',
  PROJECT_DROPDOWNS_BY_USER: `/tickets/projectDropdowns`,
  TICKETS_DROPDOWNS_CREATE: '/tickets/dropdowns',
  TICKETS_DROPDOWNS_UPDATE: (id) => `/tickets/dropdowns/${id}`,
  TICKETS_DROPDOWNS_DELETE: (id) => `/tickets/dropdowns/${id}`,
  PROJECT_STATUS_ORDER: (projectId) => `/projects/status-order/${projectId}`,
  
  // Scrum / Backlog
  SCRUM_DASHBOARD: (projectId) => `/scrum/${projectId}/backlog/dashboard`,
  SCRUM_ROLE: (projectId) => `/scrum/${projectId}/role/scrum-role`,
  SCRUM_SPRINT_MANAGEMENT: (status, projectId, type) =>
    `/scrum/sprint-details/${status}?project_id=${encodeURIComponent(projectId)}&type=${encodeURIComponent(type)}`,
  SCRUM_SPRINT_TIMELINE: (projectId) => `/scrum/${projectId}/sprint-timeline`,
  SCRUM_SPRINT_TICKETS: (projectId, sprintId, status = '') => {
    const url = `/scrum/${projectId}/sprints-tickets/${sprintId}`;
    return status ? `${url}?status=${encodeURIComponent(status)}` : url;
  },
  SCRUM_MY_TICKETS: (projectId, page = 1, limit = 15, search = '', sort = '') => {
    let url = `/scrum/my-tickets?project_id=${projectId}&page=${page}&limit=${limit}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (sort) url += `&sort=${encodeURIComponent(sort)}`;
    return url;
  },
  SCRUM_SPRINTS: (projectId) => `/scrum/${projectId}/sprints`,
  SCRUM_SPRINTS_TICKETS: (projectId, state = 'ACTIVE', sprintId = null, page = 1, limit = 15, search = '', sort = '') => {
    let url = `/scrum/${projectId}/sprints-tickets?state=${state}&page=${page}&limit=${limit}`;
    if (sprintId) url += `&sprint_id=${sprintId}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (sort) url += `&sort=${encodeURIComponent(sort)}`;
    return url;
  },
  SCRUM_SPRINT_BY_ID: (sprintId) => `/scrum/sprints/${sprintId}`,
  SCRUM_SPRINT_UPDATE: (sprintId) => `/scrum/sprints/${sprintId}/`,
  SPRINT_SETTINGS: '/settings/sprint_config',
  SCRUM_SPRINT_VERSIONS: (projectId) => `/scrum/${projectId}/sprints/versions`,
  
  // Timesheet
  TIMESHEET: '/timesheet',
  TIMESHEET_USER_DETAILS: (userId) => `/timesheet/${userId}`,
  TIMESHEET_EXPORT: '/timesheet/export',
  TIMESHEET_DROPDOWNS: (type) => `/timesheet/dropdowns?type=${type}`,
  
  // AI Dev Settings
  AI_PROJECT_SETTING: '/ai/project-setting',
  AI_PROJECT_SETTING_ALL: '/ai/project-setting/all',
  AI_PROJECT_SETTING_BY_ID: (id) => `/ai/project-setting/${id}`,
  AI_PLAN: '/ai/plan',
  AI_PLAN_BY_TASK_ID: (external_task_id) => `/ai/plan/${external_task_id}`,
  AI_PLAN_BY_ID: (id) => `/ai/plan/${id}`,
  AI_PLAN_APPROVAL: (approvalId) => `/ai/plan/approval/${approvalId}`,
  AI_CODE_GENERATION: (external_task_id) => `/ai/plan/${external_task_id}/code-generation`,
  AI_PLAN_DROPDOWN: (projectId) => `/ai/plan/dropdown?project_id=${projectId}`,
  AI_AGENT_MODEL_SETTING: '/settings/ai_models',
  
  // Strategist Agent
  AGENT_PROCESS: '/agent/process',
  AGENT_AC_CREATE: '/agent/ac/create',
  AGENT_MEMORY_FETCH: '/agent/memory/fetch',
  AGENT_MEMORY_WRITE: '/agent/memory/write',
  AGENT_REPORT: '/strategist-agent/report',
  AGENT_ALERT_CONFIGURE: '/agent/alert/configure',
  AGENT_HISTORY: '/strategist-agent/history',
  AGENT_BREAKDOWN: '/strategist-agent/breakdown',
  AGENT_GENERATE_TASK: '/strategist-agent/generateTask',
  AGENT_AC: '/strategist-agent/ac',
  AGENT_OUTPUT_UPDATE: '/strategist-agent/agent-output',
  AGENT_IMPACT: '/strategist-agent/impact',
  TICKETS_BATCH_CREATE: '/tickets/batch-create',
  
  // Frontend URLs
  TASK_URL: (ticketNumber) => `task/${ticketNumber}`,
  
  // Reports
  REPORT_TICKET_SUMMARY: '/report/ticket-summary',
  REPORT_OVERDUE_TASKS: '/report/overdue-tasks',
  REPORT_TICKETS_COUNT: '/report/tickets/count',
  REPORT_TICKETS_LIST: '/report/tickets/list',
  DAILY_STANDUP_REPORT: '/daily-scrum/report',
  DAILY_STANDUP_REPORT_EMAIL: '/daily-scrum/report/email',
  DAILY_STANDUP_LOG: (projectIds, userId, worklogFilter) => {
    const params = new URLSearchParams();

    if (projectIds) params.append('projectIds', projectIds);
    if (userId) params.append('userId', userId);
    if (worklogFilter) params.append('worklogFilter', worklogFilter);

    return `/daily-scrum/log${params.toString() ? `?${params.toString()}` : ''}`;
  },
  DAILY_STANDUP_ADD_NOTE: '/daily-scrum/log/notes',
  DAILY_STANDUP_UPDATE_NOTE: (noteId) => `/daily-scrum/log/notes/${noteId}`,
  DAILY_STANDUP_DELETE_NOTE: (noteId) => `/daily-scrum/log/notes/${noteId}`,
  
  // User Preferences
  USER_PREFERENCES: (preferenceKey) => `/user-preferences/${preferenceKey}`,
  SAVE_USER_PREFERENCES: '/user-preferences',
  
  // Settings
  GET_SETTING: (key) => `/settings/${key}`,
  GET_SETTINGS_BY_PROJECT_IDS: (key, projectIds) => `/settings/${key}?project_ids=${encodeURIComponent(projectIds.join(','))}`,
  UPDATE_SETTING: (key) => `/settings/${key}`,
  
  // Alerts
  ALERTS: '/settings/alerts',
  CREATE_ALERT: '/settings/alerts',
  UPDATE_ALERT: (id) => `/settings/alerts/${id}`,
  DELETE_ALERT: (id) => `/settings/alerts/${id}`,
  TRIGGER_ALERTS: '/settings/triggerAlerts',
  
  // Calendar
  CALENDAR: (projectId) => `/calendar/${projectId}`,
  CALENDAR_GANTT: (projectId) => `/calendar/gantt/${projectId}`,
  CALENDAR_NOTE: (projectId) => `/calendar/note/${projectId}`,
  CALENDAR_NOTE_UPDATE: (noteId) => `/calendar/note/${noteId}`,
  CALENDAR_NOTE_DELETE: (noteId) => `/calendar/note/${noteId}`,

  // Notifications
  NOTIFICATIONS_BY_USER: (userId) => `/notification/${userId}`,
  NOTIFICATIONS_READ: '/notification/read',
  NOTIFICATIONS_UNREAD_COUNT: (userId) => `/notification/unread-count/${userId}`,
  NOTIFICATIONS_STREAM: '/notification/stream',
};
